import React, { useEffect, useMemo, useState } from 'react';
import { useFormik } from 'formik';
import TextField from '@mui/material/TextField';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import { Button } from '../../../../../components/ui/button';
import InputLabel from '@mui/material/InputLabel';
import FormControl from '@mui/material/FormControl';
import { Box, Checkbox, FormControlLabel, Grid, FormHelperText, CircularProgress } from '@mui/material';
import InputMask from 'react-input-mask';
import BasicDatePicker from '../../../../../core/components/DatePicker';
import dayjs from 'dayjs';
import Autocomplete from '@mui/material/Autocomplete';
import { COUNTRY_LIST, COUNTRY_STATES } from '../constants';
import * as Yup from 'yup';
import settingsUtils from '../utils';
import { userSettingsUtils } from './utils';
import styles from './CreateUserSettings.module.scss';
import { userSettingsService } from 'core/API/userSettings';
import useUserData from '../../../../../store/useUserData';
import useSettingsStore from 'store/useSettingsStore';
import { useToastContext } from 'core/components/Toast/context/ToastContext';
import { GetWorkplaceList } from 'core/API/workplaces';
import { WorkplaceItem } from 'module/workplaces/interface/WorkplacesInterface';
import {
	getCurrentWorkplaceHierarchyNode,
	getUserFormHierarchyOptions,
	canEditUserFormHierarchy,
	resolveCurrentUserRole,
	findHierarchyNodeByResourceId,
	getHierarchyNodeResourceId,
	sortHierarchyOptionsForDisplay,
	getHierarchyOptionLabel,
	isHierarchyAssignmentPermitted,
	buildUserHierarchyFromResource,
} from './hierarchySelection';
import { formatWorkplaceUnitNumber } from 'shared/utils/stringUtils';
import {
	getAssignableSecurityRoles,
	isSecurityRoleAssignmentPermitted,
} from './securityRoleSelection';

// using settingsUtils.isValidPostalCode via form context where needed

interface CreateUserSettingsProps {
	backButtonClick: () => void;
	onUserCreated?: () => void;
}

const CreateUserSettings: React.FC<CreateUserSettingsProps> = ({
	backButtonClick,
	onUserCreated,
}) => {
	const { showToast } = useToastContext();
	const addUser = useSettingsStore((s) => s.addUser);
	const {
		securityRoles,
		hierarchyNodes,
		currentUserRole,
		refreshCurrentUserRole,
		fetchHierarchyNodes,
		hierarchyNodesLoading,
	} = useUserData();
	const [accessibleWorkplaces, setAccessibleWorkplaces] = useState<WorkplaceItem[]>([]);
	const [workplacesLoading, setWorkplacesLoading] = useState(false);

	const parseLocalWorkplaceId = () => {
		const raw = localStorage.getItem('workplace-id');
		if (!raw) return null;
		try {
			const parsed = JSON.parse(raw);
			if (typeof parsed === 'number') return parsed;
		} catch (e) {
			// ignore
		}
		const num = Number(raw);
		return isNaN(num) ? null : num;
	};

	const getHierarchyNodeType = (hierarchyResource?: string) => {
		if (!hierarchyResource) return null;
		const node = findHierarchyNodeByResourceId(hierarchyNodes, hierarchyResource);
		return node?.type ?? null;
	};

	const isOrganizationOwnedRole = (securityRoleResource?: string) => {
		if (!securityRoleResource) return false;
		const role = (securityRoles || []).find((r: any) => r?.['@id'] === securityRoleResource);
		return !role?.workplace;
	};

	const isShareAllowed = (hierarchyResource?: string, securityRoleResource?: string) => {
		const hierarchyType = getHierarchyNodeType(hierarchyResource);
		return hierarchyType === 'workplace' && isOrganizationOwnedRole(securityRoleResource);
	};

	const currentWorkplaceId = parseLocalWorkplaceId();
	const resolvedUserRole = resolveCurrentUserRole(currentUserRole);
	const currentWorkplaceHierarchy =
		currentWorkplaceId !== null
			? getCurrentWorkplaceHierarchyNode(hierarchyNodes, currentWorkplaceId)
			: undefined;
	const hierarchyOptions = useMemo(
		() =>
			currentWorkplaceId !== null
				? getUserFormHierarchyOptions(hierarchyNodes, currentWorkplaceId, resolvedUserRole)
				: hierarchyNodes,
		[hierarchyNodes, currentWorkplaceId, resolvedUserRole]
	);
	const sortedHierarchyOptions = useMemo(
		() => sortHierarchyOptionsForDisplay(hierarchyOptions, userSettingsUtils.cleanHierarchyName),
		[hierarchyOptions]
	);
	const canEditHierarchy = useMemo(
		() => canEditUserFormHierarchy(resolvedUserRole, hierarchyNodes, hierarchyOptions),
		[resolvedUserRole, hierarchyNodes, hierarchyOptions]
	);
	const assignableSecurityRoles = useMemo(
		() => getAssignableSecurityRoles(securityRoles || [], resolvedUserRole),
		[securityRoles, resolvedUserRole]
	);

	const initialFormikValues = {
		firstName: '',
		lastName: '',
		hierarchy: '',
		active: true, // Default to active for new users
		startDate: dayjs(),
		securityRole: '',
		phone: '',
		email: '',
		middleName: '',
		maritalStatus: 'Single', // Default to 'Single'
		dateOfBirth: null,
		addressLine1: '',
		addressLine2: '',
		city: '',
		state: '',
		postcode: '',
		country: 'USA',
		gender: '',
		separation: false,
		separationDate: dayjs(),
		userMustChangePasswordOnNextLogin: true, // Default to true for new users
		shareEmployee: false,
		sharedEmployeeWorkplace: '',
	};

	const validationSchema = Yup.object().shape({
		firstName: Yup.string().required('First name is required'),
		lastName: Yup.string().required('Last name is required'),
		hierarchy: Yup.string().required('Hierarchy is required'),
		active: Yup.boolean(),
		startDate: Yup.date().required('Start date is required'),
		securityRole: Yup.string().required('Security role is required'),
		phone: Yup.string()
			.required('Phone number is required')
			.test('is-complete', 'Please enter a complete phone number', (value) => {
				if (!value) return false;
				return value.replace(/[^0-9]/g, '').length === 10;
			}),
		email: Yup.string().email('Invalid email').required('Email is required'),
		middleName: Yup.string(),
		maritalStatus: Yup.string().required('Marital status is required'),
		dateOfBirth: Yup.date()
			.required('Date of birth is required')
			.max(dayjs().subtract(10, 'year').toDate(), 'User must be at least 10 years old')
			.min(dayjs().subtract(100, 'year').toDate(), 'User cannot be more than 100 years old'),
		addressLine1: Yup.string().required('Address Line 1 is required'),
		addressLine2: Yup.string(),
		city: Yup.string().required('City is required'),
		state: Yup.string(),
		postcode: Yup.string()
			.required('Postal code is required')
			.test('is-valid-postal-code', 'Invalid postal code', function (value) {
				const country = (this.parent as any)?.country;
				return !value ? false : settingsUtils.isValidPostalCode(value, country);
			}),
		country: Yup.string(),
		gender: Yup.string().required('Gender is required'),
	});

	const formik = useFormik({
		initialValues: initialFormikValues,
		validationSchema,
		onSubmit: async (values, { setSubmitting }) => {
			try {
				if (
					!isSecurityRoleAssignmentPermitted(
						values.securityRole,
						securityRoles || [],
						resolvedUserRole
					)
				) {
					showToast('error', 'You do not have permission to assign the Organization Admin role.');
					return;
				}

				if (
					!isHierarchyAssignmentPermitted(values.hierarchy, hierarchyOptions)
				) {
					showToast('error', 'You do not have permission to assign this hierarchy level.');
					return;
				}

				if (values.shareEmployee) {
					if (!isShareAllowed(values.hierarchy, values.securityRole)) {
						showToast(
							'error',
							'Employee can only be shared when hierarchy is a workplace and security role is organization owned.'
						);
						return;
					}
					if (!values.sharedEmployeeWorkplace) {
						showToast('error', 'Select a workplace to share this employee with.');
						return;
					}
				}

				// Prepare the payload for creating a new user
				const payload = {
					firstName: values.firstName,
					lastName: values.lastName,
					middleName: values.middleName,
					email: values.email ? values.email.toLowerCase() : '',
					phone: values.phone,
					gender: values.gender,
					maritalStatus: values.maritalStatus,
					dateOfBirth: values.dateOfBirth || null,
					addressLine1: values.addressLine1,
					addressLine2: values.addressLine2,
					city: values.city,
					state: values.state,
					postcode: values.postcode,
					country: values.country,
					startDate: values.startDate,
					hierarchy: values.hierarchy,
					securityRole: values.securityRole,
					active: values.active,
					separation: values.separation,
					separationDate: values.separation ? values.separationDate : null,
					userMustChangePasswordOnNextLogin: values.userMustChangePasswordOnNextLogin,
					sharedEmployeeWorkplace: values.shareEmployee
						? values.sharedEmployeeWorkplace || null
						: null,
				};

				// Call the API to create the user
				const newUser = await userSettingsService.createUser(payload);
				console.log('User created successfully:', newUser);

				const nodesForResolve =
					useSettingsStore.getState().hierarchyNodes?.length
						? useSettingsStore.getState().hierarchyNodes
						: hierarchyNodes;

				const resolvedHierarchy = buildUserHierarchyFromResource(
					values.hierarchy,
					nodesForResolve,
					newUser.hierarchy
				);

				const rolesForResolve =
					useSettingsStore.getState().securityRoles?.length
						? useSettingsStore.getState().securityRoles
						: securityRoles || [];

				const resolvedSecurityRole =
					(rolesForResolve || []).find((role: any) => role?.['@id'] === values.securityRole) ||
					newUser.securityRole;

				addUser({
					...newUser,
					id: Number(newUser.id),
					hierarchy: resolvedHierarchy as any,
					securityRole: resolvedSecurityRole as any,
					name: `${values.firstName} ${values.lastName}`.trim(),
				});

				showToast('success', 'User created successfully!');

				// Call the callback to refresh the user list
				onUserCreated?.();

				// Navigate back after a short delay
				setTimeout(() => {
					backButtonClick();
				}, 1500);
			} catch (error: any) {
				showToast('error', error.message || 'Failed to create user');
			} finally {
				setSubmitting(false);
			}
		},
	});

	const selectedHierarchyType = getHierarchyNodeType(formik.values.hierarchy);
	const selectedRoleIsOrganizationOwned = isOrganizationOwnedRole(formik.values.securityRole);
	const canShareEmployee =
		selectedHierarchyType === 'workplace' && selectedRoleIsOrganizationOwned;

	const selectedHierarchyWorkplaceId = useMemo(() => {
		const node = findHierarchyNodeByResourceId(hierarchyNodes, formik.values.hierarchy);
		if (!node || node.type !== 'workplace') return null;
		const candidate = node.childId ?? node.id;
		const parsed = Number(candidate);
		return Number.isNaN(parsed) ? null : parsed;
	}, [hierarchyNodes, formik.values.hierarchy]);

	const shareableWorkplaces = useMemo(() => {
		return (accessibleWorkplaces || []).filter((workplace) => {
			if (!workplace?.id) return false;
			if (currentWorkplaceId !== null && workplace.id === currentWorkplaceId) return false;
			if (selectedHierarchyWorkplaceId !== null && workplace.id === selectedHierarchyWorkplaceId) {
				return false;
			}
			return true;
		});
	}, [accessibleWorkplaces, currentWorkplaceId, selectedHierarchyWorkplaceId]);

	useEffect(() => {
		const loadAccessibleWorkplaces = async () => {
			try {
				setWorkplacesLoading(true);
				const response = await GetWorkplaceList();
				setAccessibleWorkplaces(response?.items || []);
			} catch (error) {
				console.error('Failed to fetch accessible workplaces:', error);
				setAccessibleWorkplaces([]);
			} finally {
				setWorkplacesLoading(false);
			}
		};

		loadAccessibleWorkplaces();
	}, []);

	useEffect(() => {
		if (!hierarchyNodes.length) {
			fetchHierarchyNodes();
		}
	}, [hierarchyNodes.length, fetchHierarchyNodes]);

	useEffect(() => {
		if (!currentUserRole) {
			refreshCurrentUserRole();
		}
	}, [currentUserRole, refreshCurrentUserRole]);

	useEffect(() => {
		if (!canShareEmployee && formik.values.shareEmployee) {
			formik.setFieldValue('shareEmployee', false, false);
			formik.setFieldValue('sharedEmployeeWorkplace', '', false);
		}
	}, [canShareEmployee, formik.values.shareEmployee, formik]);

	useEffect(() => {
		if (!formik.values.securityRole) return;
		if (
			!isSecurityRoleAssignmentPermitted(
				formik.values.securityRole,
				securityRoles || [],
				resolvedUserRole
			)
		) {
			formik.setFieldValue('securityRole', '', false);
		}
	}, [securityRoles, resolvedUserRole, formik.values.securityRole, formik]);

	useEffect(() => {
		if (canEditHierarchy || !sortedHierarchyOptions.length) return;

		const defaultNode = currentWorkplaceHierarchy ?? sortedHierarchyOptions[0];
		if (!defaultNode) return;

		const defaultResourceId = getHierarchyNodeResourceId(defaultNode);
		const currentSelection = findHierarchyNodeByResourceId(sortedHierarchyOptions, formik.values.hierarchy);

		if (!currentSelection) {
			formik.setFieldValue('hierarchy', defaultResourceId, false);
		}
	}, [
		canEditHierarchy,
		currentWorkplaceHierarchy,
		sortedHierarchyOptions,
		formik.values.hierarchy,
		formik,
	]);

	const handleSubmit = (e: React.FormEvent) => {
		e.preventDefault();
		formik.handleSubmit();
	};

	const getStateOptions = () => {
		if (!formik.values.country) return [];
		return COUNTRY_STATES[formik.values.country] || [];
	};

	const isSaving = formik.isSubmitting;

	return (
		<div className={styles.createUserSetting}>
			<Grid container direction="column" spacing={2} pl={2}>
				<Grid container direction="row" justifyContent="space-between">
					<Grid>
						<Button
							className={styles.backButton}
							onClick={backButtonClick}
							disabled={isSaving}
						>
							Back
						</Button>
					</Grid>
					<Grid item xs={6} display={'flex'} justifyContent="flex-end">
						<Grid>
							<Button
								className={styles.saveButton}
								onClick={handleSubmit}
								disabled={isSaving}
							>
								{isSaving ? 'Saving...' : 'Save'}
							</Button>
						</Grid>
					</Grid>
				</Grid>
				<Grid xs={12} container direction="row" justifyContent="flex-start" alignItems="center">
					<h2 className={styles.formTitle}>Create New User</h2>
				</Grid>
			</Grid>

			<div className={styles.formWrapper}>
				{isSaving && (
					<Box className={styles.savingOverlay}>
						<CircularProgress color="inherit" size={20} />
					</Box>
				)}

			<form className={styles.formContainer} onSubmit={formik.handleSubmit}>
				{/* User Name (Email) */}
				<TextField
					label="User Name (Email)"
					name="email"
					type="email"
					value={formik.values.email}
					onChange={formik.handleChange}
					className={styles.input}
					margin="normal"
					fullWidth
					required
					error={formik.touched.email && Boolean(formik.errors.email)}
					helperText={formik.touched.email && formik.errors.email}
				/>

				{/* First Name (Required) */}
				<TextField
					label="First Name"
					name="firstName"
					value={formik.values.firstName}
					onChange={formik.handleChange}
					className={styles.input}
					margin="normal"
					fullWidth
					required
					error={formik.touched.firstName && Boolean(formik.errors.firstName)}
					helperText={formik.touched.firstName && formik.errors.firstName}
				/>

				{/* Middle Name (Optional) */}
				<TextField
					label="Middle Name"
					name="middleName"
					value={formik.values.middleName}
					onChange={formik.handleChange}
					className={styles.input}
					margin="normal"
					fullWidth
				/>

				{/* Last Name (Required) */}
				<TextField
					label="Last Name"
					name="lastName"
					value={formik.values.lastName}
					onChange={formik.handleChange}
					className={styles.input}
					margin="normal"
					fullWidth
					required
					error={formik.touched.lastName && Boolean(formik.errors.lastName)}
					helperText={formik.touched.lastName && formik.errors.lastName}
				/>

				<BasicDatePicker
					label="Date of Birth"
					name="dateOfBirth"
					value={formik.values.dateOfBirth ? dayjs(formik.values.dateOfBirth) : null}
					onChange={(date) => formik.setFieldValue('dateOfBirth', date)}
					required
					error={formik.touched.dateOfBirth && Boolean(formik.errors.dateOfBirth)}
					helperText={(formik.touched.dateOfBirth && (formik.errors.dateOfBirth as string)) || ''}
					margin="normal"
					sx={{ fontFamily: 'Gotham' }}
				/>

				<BasicDatePicker
					label="Start Date"
					value={formik.values.startDate}
					onChange={(date) => formik.setFieldValue('startDate', date)}
					required
					error={formik.touched.startDate && Boolean(formik.errors.startDate)}
					helperText={(formik.touched.startDate && (formik.errors.startDate as string)) || ''}
				/>

				<Box
					sx={{
						border: '1px solid #ccc',
						borderRadius: 2,
						padding: 2,
						marginTop: 2,
						display: 'inline-block',
					}}
				>
					<FormControlLabel
						disabled
						control={
							<Checkbox
								checked={formik.values.userMustChangePasswordOnNextLogin}
								onChange={() => {
									formik.setFieldValue(
										'userMustChangePasswordOnNextLogin',
										!formik.values.userMustChangePasswordOnNextLogin
									);
								}}
							/>
						}
						label={'User must change password on next login'}
					/>
				</Box>

				{/* Cell Phone */}
				<InputMask
					mask="(999) 999 - 9999"
					value={formik.values.phone}
					onChange={formik.handleChange}
					maskChar="_"
				>
					{(inputProps: any) => (
						<TextField
							{...inputProps}
							label="Cell Phone"
							name="phone"
							className={styles.input}
							margin="normal"
							fullWidth
							required
							error={formik.touched.phone && Boolean(formik.errors.phone)}
							helperText={formik.touched.phone && formik.errors.phone}
						/>
					)}
				</InputMask>

				{/* Security Role (Required, Autocomplete) */}
				<Autocomplete
					options={[...assignableSecurityRoles].sort((a, b) => a.name.localeCompare(b.name))}
					getOptionLabel={(option) => (typeof option === 'string' ? option : option.name)}
					value={
						assignableSecurityRoles.find((role) => role['@id'] === formik.values.securityRole) ||
						null
					}
					onChange={(_, value) => formik.setFieldValue('securityRole', value ? value['@id'] : '')}
					isOptionEqualToValue={(option, value) => option['@id'] === value?.['@id']}
					renderInput={(params) => (
						<TextField
							{...params}
							label="Security Role"
							name="securityRole"
							required
							margin="normal"
							className={styles.input}
							error={formik.touched.securityRole && Boolean(formik.errors.securityRole)}
							helperText={formik.touched.securityRole && formik.errors.securityRole}
							fullWidth
						/>
					)}
				/>

				{/* Hierarchy */}
				<Autocomplete
					disableClearable
					loading={hierarchyNodesLoading}
					options={sortedHierarchyOptions}
					getOptionLabel={(option) =>
						getHierarchyOptionLabel(option, userSettingsUtils.cleanHierarchyName)
					}
					value={
						findHierarchyNodeByResourceId(sortedHierarchyOptions, formik.values.hierarchy) ||
						findHierarchyNodeByResourceId(hierarchyNodes, formik.values.hierarchy) ||
						null
					}
					onChange={(_, value) => {
						if (!canEditHierarchy) return;
						formik.setFieldValue(
							'hierarchy',
							value ? getHierarchyNodeResourceId(value) : ''
						);
					}}
					isOptionEqualToValue={(option, value) =>
						getHierarchyNodeResourceId(option) === getHierarchyNodeResourceId(value)
					}
					renderInput={(params) => (
						<TextField
							{...params}
							label="Hierarchy"
							name="hierarchy"
							required
							margin="normal"
							className={styles.input}
							error={formik.touched.hierarchy && Boolean(formik.errors.hierarchy)}
							helperText={
								(formik.touched.hierarchy && formik.errors.hierarchy) ||
								(!canEditHierarchy
									? 'Hierarchy is fixed to your assigned level.'
									: hierarchyNodesLoading
										? 'Loading hierarchy options...'
										: sortedHierarchyOptions.length === 0
											? 'No hierarchy options available.'
											: '')
							}
							fullWidth
							InputProps={{
								...params.InputProps,
								readOnly: !canEditHierarchy,
							}}
						/>
					)}
				/>

				<FormControlLabel
					control={
						<Checkbox
							checked={Boolean(formik.values.shareEmployee)}
							onChange={(_, checked) => {
								if (checked && !canShareEmployee) {
									showToast(
										'error',
										'Sharing is only available for employees in workplace hierarchy with organization-owned security role.'
									);
									return;
								}
								formik.setFieldValue('shareEmployee', checked);
								if (!checked) {
									formik.setFieldValue('sharedEmployeeWorkplace', '');
								}
							}}
							disabled={!canShareEmployee && !formik.values.shareEmployee}
						/>
					}
					label={'Share Employee to Another Workplace'}
				/>
				{!canShareEmployee && (
					<FormHelperText error>
						Sharing requires workplace hierarchy and an organization-owned security role.
					</FormHelperText>
				)}

				{formik.values.shareEmployee && (
					<Autocomplete
						options={shareableWorkplaces}
						loading={workplacesLoading}
						getOptionLabel={(option) =>
							typeof option === 'string'
								? option
								: `${formatWorkplaceUnitNumber(
										option.workplace_unit_number ?? option.id
									)} - ${option.name}`
						}
						value={
							shareableWorkplaces.find(
								(workplace) => workplace['@id'] === formik.values.sharedEmployeeWorkplace
							) || null
						}
						onChange={(_, value) =>
							formik.setFieldValue('sharedEmployeeWorkplace', value ? value['@id'] : '')
						}
						isOptionEqualToValue={(option, value) => option['@id'] === value?.['@id']}
						renderInput={(params) => (
							<TextField
								{...params}
								label="Shared Employee Workplace"
								name="sharedEmployeeWorkplace"
								required
								margin="normal"
								className={styles.input}
								error={
									formik.values.shareEmployee && !formik.values.sharedEmployeeWorkplace
								}
								helperText={
									formik.values.shareEmployee && !formik.values.sharedEmployeeWorkplace
										? 'Please select a workplace to share this employee with.'
										: shareableWorkplaces.length === 0
											? 'No shareable workplaces available.'
											: ''
								}
								fullWidth
							/>
						)}
					/>
				)}

				{/* Active Status */}
				<FormControlLabel
					control={
						<Checkbox
							checked={formik.values.active}
							onChange={() => {
								formik.setFieldValue('active', !formik.values.active);
							}}
						/>
					}
					label={'Active'}
				/>

				{/* Marital Status (Required, default Single) */}
				<FormControl fullWidth margin="normal" required className={styles.input}>
					<InputLabel id="marital-status-label">Marital Status</InputLabel>
					<Select
						labelId="marital-status-label"
						id="maritalStatus"
						name="maritalStatus"
						value={formik.values.maritalStatus}
						label="Marital Status"
						onChange={formik.handleChange}
					>
						<MenuItem value="Single">Single</MenuItem>
						<MenuItem value="Married">Married</MenuItem>
						<MenuItem value="Divorced">Divorced</MenuItem>
						<MenuItem value="Widowed">Widowed</MenuItem>
					</Select>
				</FormControl>

				{/* Gender (Required) */}
				<FormControl
					fullWidth
					margin="normal"
					required
					className={styles.input}
					error={formik.touched.gender && Boolean(formik.errors.gender)}
				>
					<InputLabel id="gender-label">Gender</InputLabel>
					<Select
						labelId="gender-label"
						id="gender"
						name="gender"
						value={formik.values.gender}
						label="Gender"
						onChange={formik.handleChange}
						onBlur={formik.handleBlur}
					>
						<MenuItem value="Male">Male</MenuItem>
						<MenuItem value="Female">Female</MenuItem>
						<MenuItem value="Prefer not to say">Prefer not to say</MenuItem>
					</Select>
					{formik.touched.gender && formik.errors.gender && (
						<FormHelperText>{formik.errors.gender}</FormHelperText>
					)}
				</FormControl>

				{/* Address Fields (Optional) */}
				<TextField
					label="Address Line 1"
					name="addressLine1"
					value={formik.values.addressLine1}
					onChange={formik.handleChange}
					className={styles.input}
					margin="normal"
					fullWidth
					required
					error={formik.touched.addressLine1 && Boolean(formik.errors.addressLine1)}
					helperText={formik.touched.addressLine1 && (formik.errors.addressLine1 as string)}
				/>
				<TextField
					label="Address Line 2"
					name="addressLine2"
					value={formik.values.addressLine2}
					onChange={formik.handleChange}
					className={styles.input}
					margin="normal"
					fullWidth
				/>

				<TextField
					label="City"
					name="city"
					value={formik.values.city}
					onChange={formik.handleChange}
					className={styles.input}
					margin="normal"
					fullWidth
					required
					error={formik.touched.city && Boolean(formik.errors.city)}
					helperText={formik.touched.city && formik.errors.city}
				/>


				{/* State */}
				<FormControl fullWidth margin="normal">
					<Autocomplete
						options={getStateOptions()}
						getOptionLabel={(option) => option}
						value={formik.values.state || null}
						onChange={(_, newValue) => {
							formik.setFieldValue('state', newValue || '');
						}}
						disabled={!formik.values.country}
						renderInput={(params) => (
							<TextField
								{...params}
								label="State"
								error={formik.touched.state && Boolean(formik.errors.state)}
								helperText={formik.touched.state && (formik.errors.state as string)}
								required
							/>
						)}
					/>
				</FormControl>

				{/* Postal Code */}
				<TextField
					label="Postal Code"
					name="postcode"
					value={formik.values.postcode}
					onChange={formik.handleChange}
					className={styles.input}
					margin="normal"
					fullWidth
					required
					error={formik.touched.postcode && Boolean(formik.errors.postcode)}
					helperText={formik.touched.postcode && formik.errors.postcode}
				/>

				{/* Country */}
				<FormControl fullWidth margin="normal">
					<Autocomplete
						options={COUNTRY_LIST}
						getOptionLabel={(option) => option}
						value={formik.values.country || null}
						onChange={(_, newValue) => {
							formik.setFieldValue('country', newValue || '');
							formik.setFieldValue('state', ''); // Reset state when country changes
						}}
						renderInput={(params) => (
							<TextField
								{...params}
								label="Country"
								error={formik.touched.country && Boolean(formik.errors.country)}
								helperText={formik.touched.country && (formik.errors.country as string)}
								required
							/>
						)}
					/>
				</FormControl>


				{/* <Grid
					xs={12}
					sx={{
						border: '1px solid #ccc',
						borderRadius: 2,
						padding: 2,
						marginY: 2,
						color: 'rgb(0 0 0 / 60%)',
						// backgroundColor: '#fafbfc',
						display: 'inline-block',
					}}
				>
					<FormControlLabel
						control={
							<Checkbox
								checked={formik.values.separation}
								onChange={() => {
									formik.setFieldValue('separation', !formik.values.separation);
								}}
							/>
						}
						label={'Separation'}
					/>
				</Grid>
				{formik.values.separation && (
					<BasicDatePicker
						label="Separation Date"
						value={formik.values.separationDate}
						onChange={(date) => formik.setFieldValue('separationDate', date)}
						required
						error={formik.touched.separationDate && Boolean(formik.errors.separationDate)}
						helperText={formik.touched.separationDate && formik.errors.separationDate}
					/>
				)} */}
			</form>
			</div>
		</div>
	);
};

export default CreateUserSettings;
